// Pure function — returns a pdfmake document definition.
// Caller handles dynamic import of pdfmake and font loading.

const NAVY  = '#0D2461';
const AMBER = '#F5A800';

// ── Formatters ────────────────────────────────────────────────────────────────

function fmt$(n) {
  if (n == null || isNaN(n)) return '—';
  return '$' + Math.round(n).toLocaleString();
}

function fmtPct(n) {
  if (n == null || isNaN(n)) return '—';
  return (n * 100).toFixed(1) + '%';
}

function fmtDate(dateStr) {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return String(dateStr);
  }
}

// ── Layout / style helpers ────────────────────────────────────────────────────

const tableLayout = {
  hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 1 : 0.5,
  vLineWidth: () => 0,
  hLineColor: (i) => i === 0 ? NAVY : '#e0e4ef',
  fillColor: (rowIndex) => rowIndex === 0 ? NAVY : rowIndex % 2 === 0 ? '#f5f6fa' : null,
  paddingLeft:   () => 8,
  paddingRight:  () => 8,
  paddingTop:    () => 6,
  paddingBottom: () => 6,
};

function hdr(text) {
  return { text, bold: true, fontSize: 9, color: AMBER };
}

function cell(text, opts = {}) {
  return { text: String(text ?? '—'), fontSize: 10, ...opts };
}

function para(text, opts = {}) {
  return { text, fontSize: 11, lineHeight: 1.5, margin: [0, 0, 0, 10], ...opts };
}

// Returns two content items: section title + amber rule
function sectionHeader(title) {
  return [
    { text: title, bold: true, fontSize: 13, color: NAVY, margin: [0, 16, 0, 6] },
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 532, y2: 0, lineWidth: 2, lineColor: AMBER }], margin: [0, 0, 0, 14] },
  ];
}

function pageBreak() {
  return { text: '', pageBreak: 'before' };
}

// ── Main export ───────────────────────────────────────────────────────────────

export function generateReport(data) {
  const {
    engagement   = {},
    team         = {},
    population   = {},
    sample       = {},
    scores       = {},
    results,
    attrResults,
    yearBreakdown = [],
    sampleRuns    = [],
    preparedBy    = {},
    logoBase64,
  } = data;

  const projects  = population.projects || [];
  const drawn     = sample.drawn_projects || [];
  const large100  = sample.large100 || [];
  const rawStrata = sample.strata || [];
  const isAttr    = sample.sampling_mode === 'attribute';
  const attrPlan  = sample.attr_plan;
  const settings  = sample.settings || {};
  const N         = projects.length;
  const totalAmt  = projects.reduce((s, p) => s + (p.amount || 0), 0);

  // Normalise strata — compute ni and stratumTotal defensively
  const strata = rawStrata.map(st => ({
    ...st,
    ni: st.Ni ?? st.projectCount ?? (st.projects || []).length,
    stratumTotal: st.stratumTotal
      ?? (st.projects || []).reduce((s, p) => s + (p.amount || 0), 0),
  }));

  const tossStratum   = strata.find(st => st.id === 'TOSS');
  const sampledStrata = strata.filter(st => st.id !== 'TOSS');

  const content = [];

  // ════════════════════════════════════════════════════════════════
  // COVER PAGE
  // ════════════════════════════════════════════════════════════════

  if (logoBase64) {
    content.push({ image: logoBase64, width: 120, margin: [0, 0, 0, 40] });
  }

  content.push(
    { text: 'Statistical Sampling Report', fontSize: 26, bold: true, color: NAVY, margin: [0, 0, 0, 10] },
    { text: 'Prepared pursuant to Rev. Proc. 2011-42', fontSize: 12, italics: true, color: '#5a6a8a', margin: [0, 0, 0, 50] },
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 532, y2: 0, lineWidth: 1, lineColor: '#c8d0e0' }], margin: [0, 0, 0, 40] },
    { text: engagement.client_name || '', fontSize: 22, bold: true, color: NAVY, margin: [0, 0, 0, 14] },
    { text: `Tax Years: ${engagement.tax_years || 'N/A'}`, fontSize: 14, color: '#5a6a8a', margin: [0, 0, 0, 8] },
    { text: team.name || preparedBy.firmName || '', fontSize: 12, color: '#5a6a8a', margin: [0, 0, 0, 6] },
    { text: `Generated: ${preparedBy.date || fmtDate(new Date().toISOString())}`, fontSize: 11, color: '#8a96b0', pageBreak: 'after' },
  );

  // ════════════════════════════════════════════════════════════════
  // SECTION 1 — SAMPLING PLAN (APPENDIX A)
  // ════════════════════════════════════════════════════════════════

  content.push(...sectionHeader('Section 1 — Sampling Plan (Appendix A)'));

  const numSampledStrata = sampledStrata.length;
  const threshold        = large100.length > 0 ? large100[large100.length - 1]?.amount : null;

  const stratDesc = large100.length > 0
    ? `The population was divided into ${numSampledStrata} strata using equal-interval stratification, ` +
      `plus a certainty stratum consisting of the top ${large100.length} project${large100.length !== 1 ? 's' : ''} ` +
      `by reported cost (threshold ≥ ${fmt$(threshold)}).`
    : `The population was divided into ${numSampledStrata} strata using equal-interval stratification.`;

  const fixedN = settings.fixedN || 5;
  const sizeDesc =
    settings.sizeMode === 'formula'
      ? `Sample sizes were determined using the precision formula method, targeting a relative precision of ${fmtPct(settings.targetRP || 0.15)}.`
      : settings.sizeMode === 'perStratum'
        ? 'Sample sizes were determined using per-stratum override settings specified by the practitioner.'
        : `Sample sizes were determined using the fixed method, with ${fixedN} project${fixedN !== 1 ? 's' : ''} per sampled stratum.`;

  const modeDesc = isAttr
    ? `Attribute sampling under Rev. Proc. 2011-42 §4.05. This method tests whether the error rate in the ` +
      `population exceeds a specified tolerable rate, rather than estimating a dollar amount. ` +
      `A sample of ${attrPlan?.n || 'N'} projects was required at ${fmtPct(attrPlan?.confidenceLevel || 0.95)} confidence.`
    : 'Variable sampling under Rev. Proc. 2011-42 §4.03. This method estimates the total qualified research ' +
      'expenditures using stratified random sampling combined with statistical estimation (mean, difference, ' +
      'ratio, and regression estimators are evaluated per the three-tier reporting rule).';

  content.push(
    para(`Population Definition: The population consists of ${N.toLocaleString()} research projects reported ` +
      `by ${engagement.client_name || '[Client]'} for tax year${(engagement.tax_years || '').includes(',') ? 's' : ''} ` +
      `${engagement.tax_years || 'N/A'}, with total reported costs of ${fmt$(totalAmt)}.`),
    para(`Sampling Frame: The sampling frame is coextensive with the population — all ${N.toLocaleString()} ` +
      `research projects on record for the engagement period, as uploaded to the sampling platform.`),
    para('Sampling Unit: Each research project/activity constitutes one sampling unit.'),
    para(stratDesc),
  );

  if (tossStratum) {
    content.push(para(
      `Toss Stratum: Projects in the bottom ${settings.tossPct || ''}% of reported costs were excluded from ` +
      `the sampling frame as a toss stratum, comprising ${tossStratum.ni} project${tossStratum.ni !== 1 ? 's' : ''} ` +
      `totaling ${fmt$(tossStratum.stratumTotal)}.`
    ));
  }

  content.push(
    para(sizeDesc),
    para(`Sampling Mode: ${modeDesc}`),
    para(`Random Selection: Projects were selected using a seeded pseudo-random number generator ` +
      `(seed: ${sample.seed || 'N/A'}) applied independently within each stratum, ensuring full ` +
      `reproducibility and auditability of the sample draw.`),
  );

  if (engagement.approved_by && engagement.approved_at) {
    content.push(para(
      `Approval: This sampling plan was reviewed and approved by ${engagement.approved_by} ` +
      `on ${fmtDate(engagement.approved_at)}.` +
      (engagement.approval_note ? ` Notes: ${engagement.approval_note}` : '')
    ));
  }

  // ════════════════════════════════════════════════════════════════
  // SECTION 2 — POPULATION SUMMARY
  // ════════════════════════════════════════════════════════════════

  content.push(pageBreak());
  content.push(...sectionHeader('Section 2 — Population Summary'));

  const popBody = [[
    hdr('Stratum'), hdr('Definition'), hdr('Population (Ni)'),
    hdr('Total Amount'), hdr('Sample (n)'), hdr('Sampling Rate'),
  ]];

  if (large100.length > 0) {
    const l100Total = large100.reduce((s, p) => s + (p.amount || 0), 0);
    popBody.push([
      cell('100%', { bold: true, color: '#c03a1a' }),
      cell(`Certainty (Top ${large100.length})`),
      cell(large100.length.toLocaleString(), { alignment: 'right' }),
      cell(fmt$(l100Total), { alignment: 'right' }),
      cell(large100.length.toLocaleString(), { alignment: 'right' }),
      cell('100%', { alignment: 'right' }),
    ]);
  }

  for (const st of sampledStrata) {
    const rate = st.ni > 0 ? (st.n || 0) / st.ni : 0;
    popBody.push([
      cell(st.id || '', { bold: true, color: NAVY }),
      cell(st.label || st.id || ''),
      cell(st.ni.toLocaleString(), { alignment: 'right' }),
      cell(fmt$(st.stratumTotal), { alignment: 'right' }),
      cell((st.n || 0).toLocaleString(), { alignment: 'right' }),
      cell(fmtPct(rate), { alignment: 'right' }),
    ]);
  }

  if (tossStratum) {
    popBody.push([
      cell('TOSS', { color: '#8a96b0' }),
      cell(`Excluded (bottom ${settings.tossPct || ''}% by cost)`, { color: '#8a96b0' }),
      cell(tossStratum.ni.toLocaleString(), { alignment: 'right', color: '#8a96b0' }),
      cell(fmt$(tossStratum.stratumTotal), { alignment: 'right', color: '#8a96b0' }),
      cell('0', { alignment: 'right', color: '#8a96b0' }),
      cell('—', { alignment: 'right', color: '#8a96b0' }),
    ]);
  }

  const totalSampleN = sampledStrata.reduce((s, st) => s + (st.n || 0), 0) + large100.length;
  popBody.push([
    cell('TOTAL', { bold: true }), cell(''),
    cell(N.toLocaleString(), { bold: true, alignment: 'right' }),
    cell(fmt$(totalAmt), { bold: true, alignment: 'right' }),
    cell(totalSampleN.toLocaleString(), { bold: true, alignment: 'right' }),
    cell(N > 0 ? fmtPct(totalSampleN / N) : '—', { bold: true, alignment: 'right' }),
  ]);

  content.push({
    table: { headerRows: 1, widths: [40, '*', 70, 90, 55, 70], body: popBody },
    layout: tableLayout,
    margin: [0, 0, 0, 16],
  });

  // ════════════════════════════════════════════════════════════════
  // SECTION 3 — SAMPLE DRAW DOCUMENTATION (APPENDIX B)
  // ════════════════════════════════════════════════════════════════

  content.push(pageBreak());
  content.push(...sectionHeader('Section 3 — Sample Draw Documentation (Appendix B)'));

  const firstRun = sampleRuns[0];
  content.push({
    columns: [
      { text: [{ text: 'Random Seed: ', bold: true }, String(sample.seed || 'N/A')], fontSize: 10, color: '#5a6a8a' },
      { text: [{ text: 'Draw Timestamp: ', bold: true }, firstRun ? new Date(firstRun.drawn_at).toLocaleString() : 'N/A'], fontSize: 10, color: '#5a6a8a' },
    ],
    margin: [0, 0, 0, 14],
  });

  const wpBody = [[
    hdr('#'), hdr('Project Name'), hdr('Stratum'),
    hdr('Reported Amount'), hdr('Qualified %'), hdr('Qualified Amount'),
  ]];

  const sortedDrawn = [...drawn].sort((a, b) => {
    const sA = a.isLarge ? '0' : String(a.stratum || '');
    const sB = b.isLarge ? '0' : String(b.stratum || '');
    return sA !== sB ? sA.localeCompare(sB) : (a._id || 0) - (b._id || 0);
  });

  sortedDrawn.forEach((p, i) => {
    const qualPct = scores[String(p._id)];
    const qualAmt = qualPct != null ? p.amount * qualPct / 100 : null;
    wpBody.push([
      cell(i + 1, { alignment: 'right', color: '#8a96b0' }),
      cell(p.name || '', { bold: true }),
      cell(p.isLarge ? '100% Certainty' : (p.stratum || '—')),
      cell(fmt$(p.amount), { alignment: 'right' }),
      cell(qualPct != null ? qualPct + '%' : '—', { alignment: 'right' }),
      cell(qualAmt != null ? fmt$(qualAmt) : '—', {
        alignment: 'right',
        bold: qualAmt != null,
        color: qualAmt != null ? '#1a5c3a' : '#8a96b0',
      }),
    ]);
  });

  content.push({
    table: { headerRows: 1, widths: [22, '*', 75, 85, 60, 85], body: wpBody },
    layout: tableLayout,
    margin: [0, 0, 0, 16],
  });

  // ════════════════════════════════════════════════════════════════
  // SECTION 4 — STATISTICAL RESULTS
  // ════════════════════════════════════════════════════════════════

  content.push(pageBreak());
  content.push(...sectionHeader('Section 4 — Statistical Results'));

  if (isAttr && attrResults) {
    // ── Attribute mode ──────────────────────────────────────────
    content.push(
      { text: 'Test Parameters', bold: true, fontSize: 11, color: NAVY, margin: [0, 0, 0, 8] },
      {
        table: {
          headerRows: 1, widths: [200, 100, '*'],
          body: [
            [hdr('Parameter'), hdr('Value'), hdr('Notes')],
            [cell('Confidence Level'), cell(fmtPct(attrPlan?.confidenceLevel || 0.95), { alignment: 'right' }), cell('One-sided upper limit')],
            [cell('Tolerable Error Rate'), cell(fmtPct(attrPlan?.tolerableRate), { alignment: 'right' }), cell('Maximum acceptable rate')],
            [cell('Expected Error Rate'), cell(fmtPct(attrPlan?.expectedRate || 0), { alignment: 'right' }), cell('Used for sample size calculation')],
            [cell('Required Sample Size (n)'), cell(String(attrResults.sampleSize || attrPlan?.n || '—'), { alignment: 'right' }), cell('Per Rev. Proc. 2011-42 §4.05')],
          ],
        },
        layout: tableLayout, margin: [0, 0, 0, 16],
      },
      { text: 'Test Results', bold: true, fontSize: 11, color: NAVY, margin: [0, 0, 0, 8] },
      {
        table: {
          headerRows: 1, widths: [200, 100, '*'],
          body: [
            [hdr('Metric'), hdr('Value'), hdr('Notes')],
            [cell('Errors Found'), cell(String(attrResults.errorsFound), { alignment: 'right', color: attrResults.errorsFound > 0 ? '#c03a1a' : '#1a5c3a' }), cell('Items not fully qualifying')],
            [cell('Observed Error Rate'), cell(fmtPct(attrResults.observedRate), { alignment: 'right' }), cell('Errors / n')],
            [cell('Upper Error Limit (UEL)'), cell(fmtPct(attrResults.upperErrorLimit), { alignment: 'right', color: attrResults.passesTest ? '#1a5c3a' : '#c03a1a' }), cell('Wilson score interval upper bound')],
            [cell('Projected Population Errors'), cell(String(Math.round(attrResults.projectedErrors || 0)), { alignment: 'right' }), cell(`of ${N.toLocaleString()} total projects`)],
            [cell('Test Result'), cell(attrResults.passesTest ? 'PASS' : 'FAIL', { bold: true, alignment: 'right', color: attrResults.passesTest ? '#1a5c3a' : '#c03a1a' }), cell(attrResults.passesTest ? 'UEL does not exceed tolerable rate' : 'UEL exceeds tolerable rate')],
          ],
        },
        layout: tableLayout, margin: [0, 0, 0, 16],
      },
      para(attrResults.conclusion || ''),
    );

  } else if (results) {
    // ── Variable mode ───────────────────────────────────────────
    const estMap = { Mean: results.mean, Difference: results.difference, Ratio: results.ratio, Regression: results.regression };

    const estBody = [[
      hdr('Estimator'), hdr('Point Estimate'), hdr('Std. Error'),
      hdr('Rel. Precision'), hdr('Method / Eligibility Note'), hdr('Eligible'),
    ]];

    for (const [name, est] of Object.entries(estMap)) {
      const isRec    = results.recommended === name;
      const reason   = results.ineligibleReasons?.[name];
      const rpColor  = est?.relPrecision <= 0.10 ? '#1a5c3a' : est?.relPrecision <= 0.15 ? '#c98a00' : '#c03a1a';
      estBody.push([
        cell(isRec ? name + '  ✓' : name, { bold: isRec, color: isRec ? NAVY : '#333' }),
        est ? cell(fmt$(est.pointEstimate), { alignment: 'right' }) : cell('—', { alignment: 'right', color: '#8a96b0' }),
        est ? cell(fmt$(est.se), { alignment: 'right' }) : cell('—', { alignment: 'right', color: '#8a96b0' }),
        est ? cell(fmtPct(est.relPrecision), { alignment: 'right', color: rpColor }) : cell('—', { alignment: 'right', color: '#8a96b0' }),
        est ? cell(est.estimateMethod || '—', { fontSize: 9 }) : cell(reason || 'Bias conditions not met per §4.03(3)', { fontSize: 9, color: '#8a96b0' }),
        { text: est ? 'Yes' : 'No', fontSize: 10, bold: true, color: est ? '#1a5c3a' : '#c03a1a' },
      ]);
    }

    content.push(
      { text: 'Estimator Comparison', bold: true, fontSize: 11, color: NAVY, margin: [0, 0, 0, 8] },
      { table: { headerRows: 1, widths: [100, 75, 65, 65, '*', 40], body: estBody }, layout: tableLayout, margin: [0, 0, 0, 12] },
    );

    if (Object.keys(results.ineligibleReasons || {}).length > 0) {
      const reasons = Object.entries(results.ineligibleReasons).map(([n, r]) => `${n}: ${r}`).join('; ');
      content.push({
        text: [{ text: 'Estimator Eligibility per Rev. Proc. 2011-42 §4.03(3): ', bold: true }, reasons],
        fontSize: 10, color: '#5a6a8a', lineHeight: 1.5, margin: [0, 0, 0, 12],
      });
    }

    const recResult = estMap[results.recommended] || results.mean;
    const rp        = recResult?.relPrecision;

    const threeTierText = rp <= 0.10
      ? `With a relative precision of ${fmtPct(rp)} (≤ 10%), the point estimate of ${fmt$(recResult.pointEstimate)} ` +
        `is permitted as the reported amount per Rev. Proc. 2011-42 §4.02(2)(a).`
      : rp <= 0.15
        ? `With a relative precision of ${fmtPct(rp)} (between 10% and 15%), the reported estimate of ` +
          `${fmt$(recResult.reportedEstimate)} is computed using the interpolation formula per Rev. Proc. 2011-42 §4.02(2)(b).`
        : `With a relative precision of ${fmtPct(rp)} (> 15%), the least advantageous 95% lower confidence limit of ` +
          `${fmt$(recResult.lowerLimit)} must be used as the reported amount per Rev. Proc. 2011-42 §4.02(2)(c).`;

    content.push(
      para(`Selected Estimator: The ${results.recommended} estimator is recommended per Rev. Proc. 2011-42 §4.03(1) ` +
        `as it has the smallest standard error among eligible estimators. ` +
        (results.nSampled >= 100
          ? 'A normal distribution confidence coefficient (z = 1.645) was applied.'
          : `A t-distribution confidence coefficient (z = ${recResult?.z || '—'}) was applied ` +
            `due to non-certainty sample size below 100 (n = ${results.nSampled}).`)),
      para(`Three-Tier Rule: ${threeTierText}`),
    );

    if (results.smallSampleWarning) {
      content.push({
        text: `Small Sample Disclosure: Only ${results.nSampled} non-certainty projects were scored. ` +
          `Results use the t-distribution and may have reduced precision. ` +
          `This limitation is documented per Rev. Proc. 2011-42 requirements.`,
        fontSize: 11, italics: true, color: '#c03a1a', lineHeight: 1.5, margin: [0, 0, 0, 10],
      });
    }

    content.push(
      { text: 'Confidence Interval Detail', bold: true, fontSize: 11, color: NAVY, margin: [0, 12, 0, 8] },
      {
        table: {
          headerRows: 1, widths: [210, 105, '*'],
          body: [
            [hdr('Metric'), hdr('Value'), hdr('Notes')],
            [cell('Standard Error (SE)'),           cell(fmt$(recResult?.se), { alignment: 'right' }),            cell('Of total estimated amount')],
            [cell('Confidence Coefficient'),         cell(recResult?.z === 1.645 ? '1.645' : String(recResult?.z || '—'), { alignment: 'right' }), cell(results.nSampled >= 100 ? 'Normal distribution (n ≥ 100)' : 't-distribution (n < 100)')],
            [cell('Precision (±)'),                  cell(fmt$(recResult?.precision), { alignment: 'right' }),     cell('SE × confidence coefficient')],
            [cell('Point Estimate'),                 cell(fmt$(recResult?.pointEstimate), { alignment: 'right' }), cell(results.recommended + ' estimator')],
            [cell('Reported Estimate'),              cell(fmt$(recResult?.reportedEstimate), { alignment: 'right', bold: true, color: NAVY }), cell(recResult?.estimateMethod || '')],
            [cell('Lower 95% One-Sided Limit'),      cell(fmt$(recResult?.lowerLimit), { alignment: 'right' }),    cell('Least advantageous limit')],
            [cell('Upper 95% One-Sided Limit'),      cell(fmt$(recResult?.upperLimit), { alignment: 'right' }),    cell('')],
            [cell('Relative Precision'),             cell(fmtPct(rp), { alignment: 'right', color: rp <= 0.10 ? '#1a5c3a' : rp <= 0.15 ? '#c98a00' : '#c03a1a' }), cell('Precision ÷ Point Estimate')],
            [cell('Sample Size (n, non-certainty)'), cell(String(results.nSampled || '—'), { alignment: 'right' }), cell('Non-100% strata only')],
            [cell('Random Seed'),                    cell(String(sample.seed || '—'), { alignment: 'right' }),     cell('Appendix B documentation')],
          ],
        },
        layout: tableLayout, margin: [0, 0, 0, 16],
      },
    );
  }

  // ════════════════════════════════════════════════════════════════
  // SECTION 5 — PER-TAX-YEAR QRE BREAKDOWN
  // ════════════════════════════════════════════════════════════════

  if (yearBreakdown && yearBreakdown.length > 0) {
    content.push(...sectionHeader('Section 5 — Per-Tax-Year QRE Breakdown'));

    const yearBody = [[
      hdr('Tax Year'), hdr('Population Wages ($)'), hdr('Apportionment (%)'), hdr('Estimated QREs ($)'),
    ]];

    for (const r of yearBreakdown) {
      yearBody.push([
        cell(String(r.year), { bold: true, color: NAVY }),
        cell(fmt$(r.populationAmt), { alignment: 'right' }),
        cell(fmtPct(r.apportionment), { alignment: 'right' }),
        cell(fmt$(r.estimatedQRE), { alignment: 'right', bold: true, color: '#1a5c3a' }),
      ]);
    }

    yearBody.push([
      cell('Total', { bold: true }), cell(''),
      cell('100.0%', { bold: true, alignment: 'right' }),
      cell(fmt$(yearBreakdown.reduce((s, r) => s + r.estimatedQRE, 0)), { bold: true, alignment: 'right', color: '#1a5c3a' }),
    ]);

    // Fix: push total row properly with populationAmt
    yearBody[yearBody.length - 1] = [
      cell('Total', { bold: true }),
      cell(fmt$(yearBreakdown.reduce((s, r) => s + r.populationAmt, 0)), { bold: true, alignment: 'right' }),
      cell('100.0%', { bold: true, alignment: 'right' }),
      cell(fmt$(yearBreakdown.reduce((s, r) => s + r.estimatedQRE, 0)), { bold: true, alignment: 'right', color: '#1a5c3a' }),
    ];

    content.push({
      table: { headerRows: 1, widths: [80, 148, 126, 145], body: yearBody },
      layout: tableLayout, margin: [0, 0, 0, 16],
    });
  }

  // ════════════════════════════════════════════════════════════════
  // SECTION 6 — SAMPLE RUN LOG
  // ════════════════════════════════════════════════════════════════

  if (sampleRuns && sampleRuns.length > 0) {
    content.push(...sectionHeader('Section 6 — Sample Run Log'));

    if (sampleRuns.length > 1) {
      content.push(para(
        `Note: ${sampleRuns.length} sample draws were recorded for this engagement. All draws are ` +
        `documented below per Rev. Proc. 2011-42 requirements.`
      ));
    }

    const runBody = [[
      hdr('Run #'), hdr('Date/Time'), hdr('Seed'), hdr('Sample Size'), hdr('Proj. RP'), hdr('Mode'), hdr('Settings'),
    ]];

    for (const run of sampleRuns) {
      runBody.push([
        cell('#' + (run.run_number || '—'), { bold: true, color: NAVY }),
        cell(run.drawn_at ? new Date(run.drawn_at).toLocaleString() : '—', { fontSize: 9 }),
        cell(String(run.seed || '—')),
        cell(String(run.total_sample || '—'), { alignment: 'right' }),
        cell(run.projected_rp != null ? fmtPct(run.projected_rp) : '—', { alignment: 'right' }),
        cell(run.settings?.samplingMode || 'variable'),
        cell(run.settings ? `${run.settings.numStrata || 5} strata, n=${run.settings.fixedN || 5}` : '—', { fontSize: 9, color: '#8a96b0' }),
      ]);
    }

    content.push({
      table: { headerRows: 1, widths: [30, 100, 55, 52, 50, 50, '*'], body: runBody },
      layout: tableLayout, margin: [0, 0, 0, 16],
    });
  }

  // ════════════════════════════════════════════════════════════════
  // SECTION 7 — CERTIFICATION
  // ════════════════════════════════════════════════════════════════

  content.push(pageBreak());
  content.push(...sectionHeader('Section 7 — Certification'));

  content.push(
    para('This sampling plan was designed and executed in accordance with Rev. Proc. 2011-42 and the regulations ' +
      'thereunder. The statistical methods, population definition, stratification, sample selection, and estimation ' +
      'procedures documented herein conform to the applicable requirements of Rev. Proc. 2011-42.'),
    para('The information contained in this report is based on data provided by the taxpayer and reviewed by the ' +
      'practitioner. The random sample was drawn using a reproducible, documented methodology as required for ' +
      'statistical sampling under the Revenue Procedure.'),
    { text: '', margin: [0, 24, 0, 0] },
    { text: 'Sampling Plan Prepared By:', bold: true, fontSize: 12, color: NAVY, margin: [0, 0, 0, 16] },
    {
      table: {
        widths: [110, '*'],
        body: [
          [{ text: 'Firm:',        bold: true, fontSize: 11, border: [false,false,false,false] }, { text: team.name || preparedBy.firmName || '',  fontSize: 11, border: [false,false,false,false] }],
          [{ text: 'Prepared By:', bold: true, fontSize: 11, border: [false,false,false,false] }, { text: preparedBy.fullName || '',                fontSize: 11, border: [false,false,false,false] }],
          [{ text: 'Date:',        bold: true, fontSize: 11, border: [false,false,false,false] }, { text: preparedBy.date || fmtDate(new Date().toISOString()), fontSize: 11, border: [false,false,false,false] }],
        ],
      },
      layout: 'noBorders',
      margin: [0, 0, 0, 40],
    },
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 1, lineColor: '#444' }], margin: [0, 0, 0, 6] },
    { text: 'Authorized Signature', fontSize: 10, color: '#8a96b0', margin: [0, 0, 0, 32] },
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 1, lineColor: '#444' }], margin: [0, 0, 0, 6] },
    { text: 'Date', fontSize: 10, color: '#8a96b0' },
  );

  // ════════════════════════════════════════════════════════════════
  // DOCUMENT DEFINITION
  // ════════════════════════════════════════════════════════════════

  return {
    pageSize:    'LETTER',
    pageMargins: [40, 60, 40, 60],
    content,
    footer: (currentPage, pageCount) => ({
      text:      `Page ${currentPage} of ${pageCount}`,
      alignment: 'center',
      fontSize:  9,
      color:     '#8a96b0',
      margin:    [0, 12, 0, 0],
    }),
    defaultStyle: {
      font:     'Roboto',
      fontSize: 11,
    },
  };
}
